<section class="rounded-lg border border-gray-200 bg-white p-4">
  <div
    class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
  >
    <div class="flex items-start gap-4">
      <div
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-100"
      >
        <lucide-icon name="brain" class="text-primary-500" [size]="24" />
      </div>
      <div>
        <h2 class="text-heading-2 text-greyscale-950">
          {{ "help.onboarding.quiz.sectionTitle" | transloco }}
        </h2>
        @if (!quizStarted()) {
          <div>
            <p class="text-paragraph-reg text-greyscale-600">
              {{ "help.onboarding.quiz.description" | transloco }}
            </p>
            <p class="mt-1 text-sm text-greyscale-500">
              {{
                "help.onboarding.quiz.questionCount"
                  | transloco: { count: questions.length }
              }}
            </p>
          </div>
        }
      </div>
    </div>
    @if (!quizStarted() && !showResults()) {
      <p-button (onClick)="startQuiz()">
        <lucide-icon name="play" [size]="18" />
        {{ "help.onboarding.quiz.start" | transloco }}
      </p-button>
    }
  </div>

  @if (showResults()) {
    <!-- Results Screen -->
    <div class="p-4">
      <!-- Header: score + message + retry -->
      <div class="mb-4 flex items-center justify-between">
        <div class="flex items-baseline gap-3">
          <span class="text-2xl font-bold text-greyscale-950">
            {{ score() }} / {{ questions.length }}
          </span>
          <span class="text-paragraph-reg text-greyscale-600">
            {{ getResultMessage() | transloco }}
          </span>
        </div>
        <p-button
          [label]="'help.onboarding.quiz.retry' | transloco"
          severity="secondary"
          [text]="true"
          (onClick)="restartQuiz()"
        >
          <lucide-icon name="rotate-ccw" [size]="14" />
        </p-button>
      </div>

      <div class="border-t border-greyscale-200"></div>

      <!-- Question list -->
      <div class="flex flex-col">
        @for (question of questions; track question.id; let i = $index) {
          <div class="py-3">
            <div class="flex items-start gap-2">
              <span class="text-paragraph-s-reg font-medium text-greyscale-800">
                {{ i + 1 }}. {{ question.questionKey | transloco }}
              </span>
            </div>
            <div class="mt-1 flex flex-row items-center gap-1">
              @if (userAnswers()[i] === question.correctAnswer) {
                <lucide-icon
                  name="check"
                  class="mt-0.5 shrink-0 text-primary-500"
                  [size]="16"
                />
              } @else {
                <lucide-icon
                  name="x"
                  class="mt-0.5 shrink-0 text-error-700"
                  [size]="16"
                />
              }
              <p
                class="text-paragraph-s-reg"
                [ngClass]="
                  userAnswers()[i] === question.correctAnswer
                    ? 'text-primary-600'
                    : 'text-error-700 line-through'
                "
              >
                {{ getOptionKey(question, userAnswers()[i] ?? -1) | transloco }}
              </p>
            </div>
            @if (userAnswers()[i] !== question.correctAnswer) {
              <div class="mt-1 flex flex-row items-center gap-1">
                <lucide-icon
                  name="check"
                  class="mt-0.5 shrink-0 text-primary-500"
                  [size]="16"
                />

                <p class="text-paragraph-s-reg text-primary-600">
                  {{
                    getOptionKey(question, question.correctAnswer) | transloco
                  }}
                </p>
              </div>
            }
            <div
              class="mt-2 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 p-3"
            >
              <lucide-icon
                name="lightbulb"
                class="mt-0.5 shrink-0 text-primary-500"
                [size]="16"
              />
              <p class="text-paragraph-s-reg text-greyscale-700">
                {{ question.explanationKey | transloco }}
              </p>
            </div>
          </div>
        }
      </div>
    </div>
  } @else if (quizStarted()) {
    <!-- Quiz Questions -->
    <div class="p-4">
      <!-- Progress bar -->
      <div class="mb-6">
        <div class="mb-2 flex justify-between text-sm text-greyscale-600">
          <span>{{
            "help.onboarding.quiz.questionLabel"
              | transloco: { number: currentQuestionIndex() + 1 }
          }}</span>
          <span>{{ currentQuestionIndex() + 1 }} / {{ questions.length }}</span>
        </div>
        <div class="h-2 overflow-hidden rounded-full bg-greyscale-200">
          <div
            class="h-full bg-primary-500 transition-all duration-300"
            [style.width.%]="
              ((currentQuestionIndex() + 1) / questions.length) * 100
            "
          ></div>
        </div>
      </div>

      <!-- Question -->
      <div class="mb-6">
        <h4 class="mb-4 text-heading-4 font-semibold text-greyscale-900">
          {{ currentQuestion().questionKey | transloco }}
        </h4>

        <div class="flex flex-col gap-3">
          @for (option of currentQuestion().options; track option.value) {
            <label
              [for]="'question-' + currentQuestion().id"
              [class]="
                'flex items-center gap-3 rounded-lg border-2 p-4 transition-all ' +
                getOptionClass(option.value)
              "
            >
              <p-radioButton
                [name]="'question-' + currentQuestion().id"
                [value]="option.value"
                [ngModel]="currentAnswer()"
                [disabled]="isAnswered()"
                (ngModelChange)="selectAnswer($event)"
              />
              <span class="text-paragraph-reg text-greyscale-800">
                {{ option.key | transloco }}
              </span>
              @switch (getOptionState(option.value)) {
                @case ("correct") {
                  <lucide-icon
                    name="check"
                    class="ml-auto shrink-0 text-primary-500"
                    [size]="18"
                  />
                }
                @case ("incorrect") {
                  <lucide-icon
                    name="x"
                    class="ml-auto shrink-0 text-error-700"
                    [size]="18"
                  />
                }
              }
            </label>
          }
        </div>

        @if (isAnswered()) {
          <div
            class="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 p-3"
          >
            <lucide-icon
              name="lightbulb"
              class="mt-0.5 shrink-0 text-primary-500"
              [size]="16"
            />
            <p class="text-paragraph-s-reg text-greyscale-700">
              {{ currentQuestion().explanationKey | transloco }}
            </p>
          </div>
        }
      </div>

      <!-- Navigation -->
      <div
        class="flex items-center justify-between border-t border-greyscale-200 pt-4"
      >
        <p-button
          [label]="'help.onboarding.quiz.previous' | transloco"
          icon="pi pi-arrow-left"
          severity="secondary"
          [text]="true"
          [disabled]="currentQuestionIndex() === 0"
          (onClick)="previousQuestion()"
        />

        <p-button
          [label]="
            isLastQuestion()
              ? ('help.onboarding.quiz.seeResults' | transloco)
              : ('help.onboarding.quiz.next' | transloco)
          "
          [icon]="isLastQuestion() ? 'pi pi-check' : 'pi pi-arrow-right'"
          iconPos="right"
          [disabled]="!isAnswered()"
          (onClick)="nextQuestion()"
        />
      </div>
    </div>
  }
</section>
